import React, { useState } from "react";
import { useAppLanguage } from "../hooks/useAppLanguage";
import { sendEmail } from "../services/reservationService";

const getStoredValue = (key) => localStorage.getItem(key) || "";

const supportTranslations = {
  de: {
    language: "Sprache",
    support: "Support",
    title: "Wie können wir helfen?",
    copy: "Schreiben Sie uns kurz zu Ihrem Aufenthalt. Reservierungsdetails werden automatisch ergänzt, wenn sie verfügbar sind.",
    requestSent: "Anfrage gesendet",
    successCopy: "Danke. Unser Team hat Ihre Nachricht erhalten und meldet sich so schnell wie möglich.",
    backHome: "Zurück zur Startseite",
    salutation: "Anrede",
    select: "Bitte wählen",
    mr: "Herr",
    ms: "Frau",
    mx: "Divers",
    urgency: "Dringlichkeit",
    normal: "Normal",
    urgent: "Dringend",
    firstName: "Vorname",
    lastName: "Nachname",
    phone: "Telefon optional",
    room: "Zimmer optional",
    category: "Worum geht es?",
    message: "Nachricht",
    placeholder: "Bitte beschreiben Sie das Problem und was Sie benötigen.",
    sending: "Wird gesendet...",
    submit: "Anfrage senden",
    validationName: "Bitte geben Sie Ihren Vor- und Nachnamen ein.",
    validationEmail: "Bitte geben Sie Ihre E-Mail-Adresse ein.",
    validationMessage: "Bitte beschreiben Sie, wie wir helfen können.",
    sendError: "Wir konnten Ihre Anfrage gerade nicht senden. Bitte versuchen Sie es erneut.",
    categories: {
      access: "Zugang / PIN",
      checkin: "Online Check-in",
      roomIssue: "Zimmerproblem",
      cleaning: "Reinigung",
      invoice: "Rechnung / Zahlung",
      other: "Sonstiges",
    },
  },
  en: {
    language: "Language",
    support: "Support",
    title: "How can we help?",
    copy: "Send us a short note about your stay. Reservation details are added automatically when available.",
    requestSent: "Request sent",
    successCopy: "Thank you. Our team received your message and will get back to you as soon as possible.",
    backHome: "Back to home",
    salutation: "Salutation",
    select: "Please select",
    mr: "Mr",
    ms: "Ms",
    mx: "Diverse",
    urgency: "Urgency",
    normal: "Normal",
    urgent: "Urgent",
    firstName: "First name",
    lastName: "Last name",
    phone: "Phone optional",
    room: "Room optional",
    category: "What is this about?",
    message: "Message",
    placeholder: "Please describe the issue and what you need.",
    sending: "Sending...",
    submit: "Submit request",
    validationName: "Please enter your first and last name.",
    validationEmail: "Please enter your email address.",
    validationMessage: "Please describe how we can help.",
    sendError: "We could not send your request right now. Please try again.",
    categories: {
      access: "Access / PIN",
      checkin: "Online check-in",
      roomIssue: "Room issue",
      cleaning: "Cleaning",
      invoice: "Invoice / payment",
      other: "Other",
    },
  },
};

const CustomerSupportPage = () => {
  const [language, setLanguage] = useAppLanguage();
  const t = supportTranslations[language] || supportTranslations.de;
  const [formData, setFormData] = useState({
    anrede: "",
    vorname: getStoredValue("guest-firstname"),
    nachname: getStoredValue("guest-lastname"),
    email: getStoredValue("email"),
    phone: getStoredValue("telephone"),
    roomNumber: getStoredValue("room-number"),
    reservationCode: getStoredValue("reservation-code"),
    hotelName: getStoredValue("hotel-name"),
    category: "Access / PIN",
    urgency: "Normal",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
    setError("");
  };

  const validate = () => {
    if (!formData.vorname.trim() || !formData.nachname.trim()) {
      return t.validationName;
    }
    if (!formData.email.trim()) {
      return t.validationEmail;
    }
    if (!formData.message.trim()) {
      return t.validationMessage;
    }
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const text = `
Guest Support Request:

Anrede: ${formData.anrede || "-"}
Vorname: ${formData.vorname}
Nachname: ${formData.nachname}
Email: ${formData.email}
Telefon: ${formData.phone || "-"}
Hotel: ${formData.hotelName || "-"}
Reservierung: ${formData.reservationCode || "-"}
Zimmer: ${formData.roomNumber || "-"}
Kategorie: ${formData.category}
Dringlichkeit: ${formData.urgency}

Nachricht:
${formData.message}
    `.trim();

    setStatus("submitting");
    setError("");

    try {
      await sendEmail({
        from: "guestified",
        subject: "New Guest Support Request",
        email: formData.email,
        to: "rocholl@rocholl-group.de",
        text,
      });

      setStatus("success");
    } catch (submitError) {
      console.error("Failed to submit support request", submitError);
      setStatus("idle");
      setError(t.sendError);
    }
  };

  if (status === "success") {
    return (
      <main className="support-page">
        <section className="support-shell">
          <p className="support-kicker">{t.support}</p>
          <h1>{t.requestSent}</h1>
          <p className="support-copy">{t.successCopy}</p>
          <a className="support-home-link" href="/home">
            {t.backHome}
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="support-page">
      <section className="support-shell">
        <div className="page-header-top">
          <p className="support-kicker">{t.support}</p>
          <label className="home-language-switcher">
            <span className="visually-hidden">{t.language}</span>
            <select value={language} onChange={(event) => setLanguage(event.target.value)}>
              <option value="de">DE</option>
              <option value="en">EN</option>
            </select>
          </label>
        </div>
        <h1>{t.title}</h1>
        <p className="support-copy">{t.copy}</p>

        <form className="support-form" onSubmit={handleSubmit}>
          <div className="support-grid two">
            <label>
              <span>{t.salutation}</span>
              <select value={formData.anrede} onChange={(event) => updateField("anrede", event.target.value)}>
                <option value="">{t.select}</option>
                <option value="Herr">{t.mr}</option>
                <option value="Frau">{t.ms}</option>
                <option value="Divers">{t.mx}</option>
              </select>
            </label>
            <label>
              <span>{t.urgency}</span>
              <select value={formData.urgency} onChange={(event) => updateField("urgency", event.target.value)}>
                <option value="Normal">{t.normal}</option>
                <option value="Urgent">{t.urgent}</option>
              </select>
            </label>
          </div>

          <div className="support-grid two">
            <label>
              <span>{t.firstName}</span>
              <input value={formData.vorname} onChange={(event) => updateField("vorname", event.target.value)} />
            </label>
            <label>
              <span>{t.lastName}</span>
              <input value={formData.nachname} onChange={(event) => updateField("nachname", event.target.value)} />
            </label>
          </div>

          <label>
            <span>Email</span>
            <input
              type="email"
              value={formData.email}
              onChange={(event) => updateField("email", event.target.value)}
            />
          </label>

          <div className="support-grid two">
            <label>
              <span>{t.phone}</span>
              <input value={formData.phone} onChange={(event) => updateField("phone", event.target.value)} />
            </label>
            <label>
              <span>{t.room}</span>
              <input value={formData.roomNumber} onChange={(event) => updateField("roomNumber", event.target.value)} />
            </label>
          </div>

          <label>
            <span>{t.category}</span>
            <select value={formData.category} onChange={(event) => updateField("category", event.target.value)}>
              <option value="Access / PIN">{t.categories.access}</option>
              <option value="Online check-in">{t.categories.checkin}</option>
              <option value="Room issue">{t.categories.roomIssue}</option>
              <option value="Cleaning">{t.categories.cleaning}</option>
              <option value="Invoice / payment">{t.categories.invoice}</option>
              <option value="Other">{t.categories.other}</option>
            </select>
          </label>

          <label>
            <span>{t.message}</span>
            <textarea
              value={formData.message}
              onChange={(event) => updateField("message", event.target.value)}
              placeholder={t.placeholder}
            />
          </label>

          {error && <p className="support-error">{error}</p>}

          <button className="support-submit" type="submit" disabled={status === "submitting"}>
            {status === "submitting" ? t.sending : t.submit}
          </button>
        </form>
      </section>
    </main>
  );
};

export default CustomerSupportPage;
